/**
 * Parties: customers and suppliers.
 *
 * WHY THIS SERVICE EXISTS NOW
 * ---------------------------
 * There was no write path for parties at all — only `partyReader`. Sync cannot be
 * proven for a record nothing can create, so this is the minimum write service the
 * wiring needs, and it follows the same atomicity rule as the document and
 * payment paths.
 *
 * DELETION IS A TOMBSTONE, NOT A ROW REMOVAL
 * -------------------------------------------
 * `deleteParty` writes a DELETE mutation and marks the row inactive. It does NOT
 * remove the row. A device offline when a customer is deleted still holds that
 * customer, and on reconnect pushes its local copy — resurrecting it. The
 * tombstone is what beats that, and the row is kept so a document that referenced
 * this customer can still print the name and GSTIN that were on it when issued.
 */
import type { Table } from 'dexie';
import { Dexie } from 'dexie';
import { ulid } from '@platform/ids';
import type { Party, SyncOutboxEntry } from '../db/types';
import { recordBusinessMutation, nextVersion } from '../sync/recordBusinessMutation';
import {
  emailField,
  gstinField,
  partyNameField,
  phoneField,
  stateCodeField,
  stateMatchesGstin,
} from '@features/masterData/masterDataRules';

/** The tables a party write may touch. `syncOutbox` is required, not optional. */
export interface PartyTx {
  parties: Table<Party, string>;
  syncOutbox: Table<SyncOutboxEntry, string>;
  table(name: string): Table<Record<string, unknown>, string>;
}

export interface CreatePartyInput {
  readonly businessId: string;
  readonly kind: Party['kind'];
  readonly name: string;
  readonly code?: string;
  readonly gstin?: string;
  readonly stateCode?: string;
  readonly stateName?: string;
  /** The billing address, and the default delivery address. */
  readonly addressLines?: readonly string[];
  /**
   * A delivery address that DIFFERS from the billing one.
   *
   * Separate on purpose, and load-bearing for place of supply: the resolver
   * reads the delivery state, so a customer who receives at a site in another
   * state is expressed here rather than by rewriting their registered address.
   */
  readonly shippingAddressLines?: readonly string[];
  readonly shippingStateCode?: string;
  readonly shippingStateName?: string;
  readonly phone?: string;
  readonly email?: string;
  readonly openingBalancePaise?: number;
  readonly user: { readonly id: string; name: string };
  readonly now: string;
  readonly deviceId: string;
}

export interface UpdatePartyInput extends Partial<CreatePartyInput> {
  readonly id: string;
  readonly businessId: string;
  readonly now: string;
  readonly deviceId: string;
}

export type PartyErrorCode =
  | 'UNKNOWN_PARTY'
  | 'WRONG_BUSINESS'
  | 'NAME_REQUIRED'
  | 'INVALID_GSTIN'
  | 'INVALID_STATE_CODE'
  | 'GSTIN_STATE_MISMATCH'
  | 'INVALID_EMAIL'
  | 'INVALID_PHONE'
  | 'DUPLICATE_GSTIN'
  | 'DUPLICATE_SOFT';

function fail(code: PartyErrorCode, message: string): Error & { code: PartyErrorCode } {
  return Object.assign(new Error(message), { code, name: 'PartyServiceError' });
}

/**
 * The field rules, run against the values AS TYPED.
 *
 * The form runs these on every keystroke and this runs them again on submit. That
 * is not duplication: a form is a suggestion, and a service that trusts it would
 * accept whatever the caller sent. The rule is defined once, in
 * `masterDataRules`; only the TIMING differs.
 */
function validatePartyFields(input: {
  readonly name?: string;
  readonly gstin?: string;
  readonly stateCode?: string;
  readonly email?: string;
  readonly phone?: string;
}) {
  const name = partyNameField(input.name ?? '');
  if (!name.ok) throw fail('NAME_REQUIRED', name.message ?? 'A customer needs a name.');

  const gstin = gstinField(input.gstin ?? '');
  if (!gstin.ok) throw fail('INVALID_GSTIN', gstin.message ?? 'That GSTIN is not valid.');

  const state = stateCodeField(input.stateCode ?? '');
  if (!state.ok) throw fail('INVALID_STATE_CODE', state.message ?? 'That state code is not valid.');

  const match = stateMatchesGstin(input.stateCode, input.gstin);
  if (!match.ok)
    throw fail('GSTIN_STATE_MISMATCH', match.message ?? 'The GSTIN and state disagree.');

  const email = emailField(input.email ?? '');
  if (!email.ok) throw fail('INVALID_EMAIL', email.message ?? 'That email is not valid.');

  const phone = phoneField(input.phone ?? '');
  if (!phone.ok) throw fail('INVALID_PHONE', phone.message ?? 'That phone number is not valid.');
}

/**
 * Whether another ACTIVE party in this company already claims this GSTIN.
 *
 * ONLY OBJECTIVE IDENTITIES ARE ENFORCED, and the GSTIN is the only one a party
 * has. It is not fuzzy matching and it is not a name comparison: two parties with
 * one GSTIN are, by definition, the same legal entity, and letting both exist
 * means the same company's invoices split across two ledgers with no way to tell
 * which is which.
 *
 * Inactive rows are excluded deliberately. A deactivated customer keeps its
 * historical invoices, and re-activating them later must not collide with the
 * row they were deactivated alongside.
 *
 * `ignoreId` is what makes an EDIT of that same party pass: without it, saving
 * an unchanged customer would report itself as a duplicate of itself.
 *
 * NOT `async`, and that is load-bearing rather than a style preference. This is
 * called from inside a live Dexie transaction, and Dexie decides a transaction may
 * commit when it can no longer see an outstanding promise OF ITS OWN. An `async`
 * function hands back a promise Dexie cannot see, so it concludes the transaction
 * is finished while the write is still in flight — and the business row commits
 * with the outbox EMPTY. See the same reasoning, at more length, in
 * `recordBusinessMutation`.
 */
export function findDuplicateGstin(
  tx: Pick<PartyTx, 'parties'>,
  input: { readonly businessId: string; readonly gstin?: string; readonly ignoreId?: string },
): Dexie.Promise<Party | null> {
  const gstin = input.gstin?.trim().toUpperCase();
  if (!gstin) return Dexie.Promise.resolve(null);
  return tx.parties
    .where('businessId')
    .equals(input.businessId)
    .toArray()
    .then(
      (rows) =>
        rows.find(
          (party) =>
            party.id !== input.ignoreId &&
            party.isActive &&
            party.gstin?.trim().toUpperCase() === gstin,
        ) ?? null,
    );
}

/**
 * A SOFT duplicate: same name, same phone, same email as an existing ACTIVE party.
 *
 * Unlike `findDuplicateGstin`, this does NOT hard-block — two legitimate entries
 * can share a phone or email (e.g. an office line, or a personal address for a
 * freelancer). But a customer who is genuinely the same person typed into the
 * ledger twice wastes time and risks split invoices, so the service surfaces the
 * match as a warning and lets the caller decide.
 *
 * ALL THREE FIELDS must be present and match. A name-only match is too noisy —
 * "Ramesh" is a common name — and a phone-or-email match on a blank value is a
 * false positive. Blank phone or email short-circuits to `null`, exactly as the
 * GSTIN check does.
 *
 * Phone is compared on digits only, so "(900) 123 4567" and "9001234567" agree.
 * Name and email are case-folded and trimmed.
 */
export function findDuplicateByNamePhoneEmail(
  tx: Pick<PartyTx, 'parties'>,
  input: {
    readonly businessId: string;
    readonly name: string;
    readonly phone?: string;
    readonly email?: string;
    readonly ignoreId?: string;
  },
): Dexie.Promise<Party | null> {
  const name = input.name?.trim().toLowerCase();
  if (!name) return Dexie.Promise.resolve(null);
  // Both phone and email must be present for the match to fire. With only one
  // filled in the risk of a false positive is too high, and the user gets a
  // warning every time they enter a colleague's extension number.
  if (!input.phone || !input.email) return Dexie.Promise.resolve(null);
  const email = input.email.trim().toLowerCase();
  const phone = input.phone.replace(/\D/g, '');
  if (phone.length < 6) return Dexie.Promise.resolve(null);

  return tx.parties
    .where('businessId')
    .equals(input.businessId)
    .toArray()
    .then(
      (rows) =>
        rows.find(
          (party) =>
            party.id !== input.ignoreId &&
            party.isActive &&
            party.name.trim().toLowerCase() === name &&
            party.email?.trim().toLowerCase() === email &&
            party.phone?.replace(/\D/g, '') === phone,
        ) ?? null,
    );
}

/**
 * The result of creating or updating a party, carrying an optional soft-duplicate
 * warning.
 *
 * When `party` is `null`, the write was SKIPPED because a soft-duplicate was
 * found and `force` was not set. The caller must ask the user whether to
 * proceed before retrying with `force = true`.
 *
 * `potentialDuplicate` is another ACTIVE party in the same business that
 * shares name + phone + email, or `null` when no match was found.
 */
export interface PartyWriteResult {
  readonly party: Party | null;
  readonly potentialDuplicate: Party | null;
}

/**
 * Create a customer or supplier, and record the mutation in the SAME
 * transaction.
 *
 * THE CHANGE ID IS MINTED, NOT DERIVED
 * -----------------------------------
 * `recordBusinessMutation` generates a fresh ULID per call. That is not an
 * implementation detail: a DERIVED id — `party:<id>`, say — can be reconstructed
 * by another device from the row it already holds, so two devices mint the same
 * change id for the same event and the provider, which keys idempotency on
 * `changeId` alone, collapses the second as a duplicate. The create then never
 * reaches the other device and the shop has two customers on two devices. See
 * `docs/sync-architecture.md`, "A change id identifies ONE mutation, and is
 * minted — never derived".
 *
 * THE SOFT-DUPLICATE CHECK RUNS BEFORE THE WRITE
 * -----------------------------------------------
 * `findDuplicateByNamePhoneEmail` runs BEFORE `writeParty`, so a soft match
 * returns `{ party: null, potentialDuplicate }` and does NOT persist a duplicate
 * row. The caller (the UI) asks the user whether to proceed; only when the user
 * explicitly confirms ("Create anyway") is `force = true` passed to skip the
 * pre-check and write the row. GSTIN, by contrast, is enforced as a HARD block
 * above — it is an objective identity, and two customers cannot share one.
 *
 * Every step is a `.then()` in ONE promise chain so Dexie can see the whole
 * transaction and commit the row together with its outbox entry.
 */
export function createParty(
  tx: PartyTx,
  input: CreatePartyInput,
  force = false,
): Dexie.Promise<PartyWriteResult> {
  validatePartyFields(input);
  return findDuplicateGstin(tx, {
    businessId: input.businessId,
    gstin: input.gstin,
  })
    .then((duplicate) => {
      if (duplicate) {
        throw fail(
          'DUPLICATE_GSTIN',
          `GSTIN ${input.gstin?.trim().toUpperCase()} already belongs to ${duplicate.name}. ` +
            'Two customers cannot share one GSTIN, because it is the same legal entity.',
        );
      }
    })
    .then(() =>
      force
        ? Dexie.Promise.resolve(null)
        : findDuplicateByNamePhoneEmail(tx, {
            businessId: input.businessId,
            name: input.name,
            phone: input.phone,
            email: input.email,
          }),
    )
    .then((potentialDuplicate) => {
      if (potentialDuplicate) {
        return { party: null, potentialDuplicate } as PartyWriteResult;
      }
      return writeParty(tx, input).then((party) => ({ party, potentialDuplicate: null }));
    });
}

/**
 * The actual insert, once the duplicate question is answered.
 *
 * Split out so the whole of `createParty` is ONE promise chain Dexie can follow.
 * An `async` function returns a promise Dexie cannot see, which lets it decide the
 * transaction is finished while `recordBusinessMutation` is still in flight — and
 * the row then commits with an EMPTY outbox, which is the worst state this system
 * can reach.
 */
function writeParty(tx: PartyTx, input: CreatePartyInput): Dexie.Promise<Party> {
  const id = ulid();
  const party = {
    id,
    businessId: input.businessId,
    kind: input.kind,
    name: input.name.trim(),
    // Required and non-optional on `Party`, so it is always written — an empty
    // string is a real "not numbered yet" state.
    code: input.code ?? '',
    gstin: input.gstin,
    stateCode: input.stateCode,
    stateName: input.stateName,
    // `billingAddressLines` and NOT `addressLines`. The column is required and
    // non-optional on `Party`, and `toPartySnapshot` iterates it when an invoice
    // is raised — so writing the wrong key produced a customer that could be
    // saved and then blew up the moment anyone invoiced them. A default of `[]`
    // rather than `undefined`, because "no address yet" is a real state and must
    // still be a well-formed row.
    billingAddressLines: input.addressLines ? [...input.addressLines] : [],
    shippingAddressLines: input.shippingAddressLines ? [...input.shippingAddressLines] : undefined,
    shippingStateCode: input.shippingStateCode,
    shippingStateName: input.shippingStateName,
    creditDays: 0,
    tags: [],
    phone: input.phone,
    email: input.email,
    openingBalancePaise: input.openingBalancePaise ?? 0,
    isActive: true,
    createdAt: input.now,
    createdBy: input.user.id,
    updatedAt: input.now,
    updatedBy: input.user.id,
    syncVersion: 1,
  } as unknown as Party;

  return tx.parties.put(party).then(() =>
    recordBusinessMutation(tx, {
      businessId: input.businessId,
      deviceId: input.deviceId,
      entityType: 'party',
      entityId: id,
      operation: 'UPSERT',
      version: 1,
      changedAt: input.now,
      // No `changeId` here — the recorder mints one per mutation.
      //
      // Deriving it from the version instead is precisely what let two devices
      // hand the provider the SAME id for two DIFFERENT edits, and the provider
      // then kept whichever arrived first. See `recordBusinessMutation.ts`.
      payload: party as unknown as Record<string, unknown>,
    }).then(() => party),
  );
}

/**
 * Edit a party, and record the mutation in the same transaction.
 *
 * NOT `async`, for the reason given on `writeParty`: an `async` function returns a
 * promise Dexie cannot see, so it may commit the transaction while the outbox write
 * is still in flight.
 *
 * The version comes from the ROW, incremented. Two devices editing the same
 * customer produce version 2 and version 2, and `conflictModel.ts` can then tell
 * "disjoint fields, mergeable" from "same field, ask a human". A clock would
 * instead make the two edits look ordered by wall time, which lies the moment one
 * of those devices was asleep.
 *
 * The name+phone+email soft-duplicate check runs BEFORE the write, the same way
 * `createParty` does: if another active party already matches the merged values
 * and `force` was not set, the write is skipped and the result carries
 * `party: null` plus the match so the caller can prompt the user. GSTIN remains a
 * hard block above.
 *
 * Returns the same `PartyWriteResult` shape as `createParty`: the merged row (or
 * `null` when skipped) plus an optional soft-duplicate warning.
 */
export function updateParty(
  tx: PartyTx,
  input: UpdatePartyInput,
  force = false,
): Dexie.Promise<PartyWriteResult> {
  return tx.parties.get(input.id).then((existing) => {
    if (!existing) {
      throw fail('UNKNOWN_PARTY', `No customer or supplier with id "${input.id}".`);
    }
    if (existing.businessId !== input.businessId) {
      throw fail(
        'WRONG_BUSINESS',
        'That customer belongs to a different company. Refusing, because editing it here ' +
          "would change another business's records.",
      );
    }

    const version = nextVersion(existing.syncVersion);
    const mergedInput = {
      name: input.name?.trim() ?? existing.name,
      gstin: input.gstin ?? existing.gstin,
      stateCode: input.stateCode ?? existing.stateCode,
      email: input.email ?? existing.email,
      phone: input.phone ?? existing.phone,
    };
    validatePartyFields(mergedInput);

    return findDuplicateGstin(tx, {
      businessId: input.businessId,
      gstin: mergedInput.gstin,
      ignoreId: existing.id,
    })
      .then((duplicate) => {
        if (duplicate) {
          throw fail(
            'DUPLICATE_GSTIN',
            `GSTIN ${mergedInput.gstin?.trim().toUpperCase()} already belongs to ${duplicate.name}. ` +
              'Two customers cannot share one GSTIN, because it is the same legal entity.',
          );
        }
      })
      .then(() =>
        force
          ? Dexie.Promise.resolve(null)
          : findDuplicateByNamePhoneEmail(tx, {
              businessId: input.businessId,
              name: mergedInput.name,
              phone: mergedInput.phone,
              email: mergedInput.email,
              ignoreId: existing.id,
            }),
      )
      .then((potentialDuplicate) => {
        if (potentialDuplicate) {
          return { party: null, potentialDuplicate } as PartyWriteResult;
        }

        const merged = {
          ...existing,
          name: input.name?.trim() ?? existing.name,
          code: input.code ?? existing.code,
          gstin: input.gstin ?? existing.gstin,
          stateCode: input.stateCode ?? existing.stateCode,
          stateName: input.stateName ?? existing.stateName,
          billingAddressLines: input.addressLines
            ? [...input.addressLines]
            : existing.billingAddressLines,
          shippingAddressLines: input.shippingAddressLines
            ? [...input.shippingAddressLines]
            : existing.shippingAddressLines,
          shippingStateCode: input.shippingStateCode ?? existing.shippingStateCode,
          shippingStateName: input.shippingStateName ?? existing.shippingStateName,
          phone: input.phone ?? existing.phone,
          email: input.email ?? existing.email,
          updatedAt: input.now,
          updatedBy: input.user?.id ?? existing.updatedBy,
          syncVersion: version,
        } as Party;

        return tx.parties
          .put(merged)
          .then(() =>
            recordBusinessMutation(tx, {
              businessId: input.businessId,
              deviceId: input.deviceId,
              entityType: 'party',
              entityId: input.id,
              operation: 'UPSERT',
              version,
              changedAt: input.now,
              payload: merged as unknown as Record<string, unknown>,
            }).then(() => merged),
          )
          .then((party) => ({ party, potentialDuplicate: null }));
      });
  });
}

/**
 * Deactivate a party and publish a TOMBSTONE.
 *
 * The row is marked inactive rather than removed, for the reason in the file
 * header: a document that referenced this customer must still be able to print
 * the name and GSTIN that were on it when it was issued.
 *
 * NOT `async` — same reason as `writeParty`. An `async` wrapper would let Dexie
 * commit before the tombstone reached the outbox, leaving a deactivated row whose
 * delete never syncs and which an offline device would keep.
 */
export function deleteParty(
  tx: PartyTx,
  input: { id: string; businessId: string; now: string; deviceId: string },
): Dexie.Promise<void> {
  return tx.parties.get(input.id).then((existing) => {
    if (!existing) return undefined;
    if (existing.businessId !== input.businessId) {
      throw fail('WRONG_BUSINESS', 'That customer belongs to a different company.');
    }

    const version = nextVersion(existing.syncVersion);
    return tx.parties
      .put({ ...existing, isActive: false, syncVersion: version } as Party)
      .then(() =>
        recordBusinessMutation(tx, {
          businessId: input.businessId,
          deviceId: input.deviceId,
          entityType: 'party',
          entityId: input.id,
          operation: 'DELETE',
          version,
          changedAt: input.now,
          // Required: a tombstone with no timestamp cannot be aged out during
          // compaction, so it would sit in the change log forever.
          deletedAt: input.now,
        }).then(() => undefined),
      );
  });
}
